import { Typography, Box, Link, IconButton } from '@mui/material';
import * as FoActions from 'features/frontoffice/actions/actions';
import React, { useEffect } from 'react';
import { IconArrowNarrowRight } from '@tabler/icons-react';
import { useSelector } from 'react-redux';
import { hasTenantDataRefreshPermission } from 'utils/common';
import { store } from 'store/store';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import StyledTooltip from 'common/StyledTooltip';
import InfoIcon from 'icons/InfoIcon';

const DATA_AVAILABLE_TOOLTIP_TEXT =
  'Claims paid through this date are available for reporting. Date filters cannot extend beyond it.';

let lastInspectionTenantCd = null;
let lastInspectionRequestTs = 0;
const INSPECTION_REQUEST_DEDUPE_WINDOW_MS = 2000;

const baseTextStyle = {
  color: '#121A22',
  leadingTrim: 'both',
  textEdge: 'cap',
  fontFamily: 'Noto Sans',
  fontSize: '14px',
  fontStyle: 'normal',
  lineHeight: '150%'
};

const Inspection = ({ isNewHome, showRefreshStatus = false, isReports = false }) => {
  const inspection = useSelector((state) => state.fo.inspection);
  const currentTenantCd = useSelector((state) => state.user.currentTenantCd);
  const user = useSelector((state) => state.user);
  const location = useLocation();
  const dataThroughDate = inspection?.dataThroughDate;
  const estimatedRefreshDate = inspection?.estimatedRefreshDate;

  useEffect(() => {
    if (!currentTenantCd) {
      return;
    }

    const now = Date.now();
    if (
      currentTenantCd === lastInspectionTenantCd &&
      now - lastInspectionRequestTs < INSPECTION_REQUEST_DEDUPE_WINDOW_MS
    ) {
      return;
    }

    lastInspectionTenantCd = currentTenantCd;
    lastInspectionRequestTs = now;
    store.dispatch(FoActions.getInspectionForTenant(currentTenantCd));
  }, [currentTenantCd]);

  return (
    <>
      <Box
        className={isNewHome || isReports ? '' : 'site-main__top'}
        sx={{
          backgroundColor: isNewHome ? '#EFF5FF' : '#FFFFFF',
          paddingTop: {
            xs: isReports ? '32px' : '0px',
            sm: isReports ? '16px' : '0px',
            md: isReports ? '16px' : '0px',
            lg: isReports ? '16px' : (isNewHome ? '0px' : '40px'),
            xl: isReports ? '16px' : (isNewHome ? '0px' : '40px'),
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            alignItems: 'center',
            ...(isNewHome || isReports ? { marginBottom: '24px' } : { float: 'right' }),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
            <Typography sx={{ ...baseTextStyle, fontWeight: '600' }}>Data Available Through: </Typography>
            <Typography sx={{ ...baseTextStyle, fontWeight: '400' }}>{dataThroughDate || 'N/A'}</Typography>
            <StyledTooltip
              title={DATA_AVAILABLE_TOOLTIP_TEXT}
              placement="bottom-start"
              enterTouchDelay={0}
              leaveTouchDelay={4000}
              tooltipSx={{
                background: '#121A22',
                width: '308px',
                maxWidth: 'none',
                boxSizing: 'border-box',
                padding: '16px',
                borderRadius: '0px 8px 8px 8px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '8px',
                lineHeight: '150%',
                leadingTrim: 'both',
                textEdge: 'cap',
                              }}
              arrowSx={{
                left: 0,
                pointerEvents: 'none',
                right: 'auto !important',
                transform: 'none !important',
                '&::before': {
                  border: 'none',
                  backgroundColor: 'transparent',
                  width: 0,
                  height: 0,
                  borderRight: '15px solid transparent',
                  borderBottom: '15px solid #121A22',
                  position: 'absolute',
                  left: 0,
                  right: 'auto !important',
                  transform: 'none !important',
                },
              }}
              popperModifiers={[{ name: 'offset', options: { offset: [18, -9] } }]}
            >
              <IconButton
                size="small"
                disableRipple
                sx={{ cursor: 'pointer' }}
                aria-label="Data available through info"
              >
                <InfoIcon />
              </IconButton>
            </StyledTooltip>
          </Box>
          {estimatedRefreshDate && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography sx={{ ...baseTextStyle, fontWeight: '600' }}>Estimated Refresh Date: </Typography>
              <Typography sx={{ ...baseTextStyle, fontWeight: '400' }}>{estimatedRefreshDate}</Typography>
            </Box>
          )}
          {showRefreshStatus && hasTenantDataRefreshPermission(user) && (
            <Link
              component={RouterLink}
              to="/tenant-refresh"
              state={{ from: `${location.pathname}${location.search}${location.hash}` }}
              underline="none"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                width: 'fit-content',
                color: '#2F54A5',
                fontFamily: 'Noto Sans',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: '600',
                letterSpacing: '0%',
                background: 'none',
                border: 'none',
                padding: 0,
                '&:hover': { color: '#068DF1', textDecoration: 'none', background: 'transparent' },
              }}
            >
              View Refresh Status
              <IconArrowNarrowRight size={16} />
            </Link>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Inspection;
